import React, { useEffect, useRef, useState } from 'react';
import { withReportChips } from '../../reportChips';
import Grid from '@material-ui/core/Grid';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import GenericTable from '../../../../Components/GenericTable';
import { Pagination, PaginationItem } from '@material-ui/lab';
import CustamizedFilters from '../../../../Components/CustamizedFilters';
import {
    GetApp as GetAppIcon,
    Print,
} from '@material-ui/icons';
import { Button, CircularProgress, IconButton, Input, TextField, Tooltip, Typography } from '@material-ui/core';
import FilterModal from '../FilterModal';
import {
    DEFAULT_PAGE_SIZE,
    PAGE_SIZE_OPTIONS,
    formatShowingRange,
    resolvePagination,
} from '../../learningReportPagination';

export const NOT_STARTED_TOOLTIP = 'This counts licences and Learning Hub enrolments that have never appeared in this report. It is not a count of people. One person with three unused courses counts as 3. Customer and project filters apply. Course, certificate, email, and date filters do not. These assignments are not listed in the table.';

const useStyles = makeStyles((theme) => ({
    actionIconBtn: {
        width: 36,
        height: 36,
        borderRadius: 8,
        border: '1px solid #e7ebf0',
        backgroundColor: '#fff',
        '&:hover': {
            backgroundColor: 'rgba(61, 125, 221, 0.06)',
            borderColor: '#d5dde8',
        },
    },
    titleRow: {
        display: 'flex',
        alignItems: 'baseline',
        flexWrap: 'wrap',
        gap: theme.spacing(1.5),
        width: '100%',
        marginBottom: theme.spacing(1.5),
    },
    reportTitle: {
        color: 'var(--dm-text-strong, #263548)',
        fontSize: '1.0625rem',
        fontWeight: 700,
        lineHeight: 1.35,
    },
    notStartedCount: {
        color: 'var(--dm-muted, #647184)',
        fontSize: '0.8125rem',
        fontWeight: 600,
        lineHeight: 1.4,
    },
    tableWrapper: {
        width: '100%',
        overflowX: 'auto',
        marginTop: theme.spacing(1),
    },
    paginationRow: {
        marginTop: 22,
        marginBottom: 20,
        [theme.breakpoints.down('xs')]: {
            marginTop: 16,
            marginBottom: 16,
        },
    },
    paginationWrap: {
        display: 'flex',
        margin: '0 auto',
        flexWrap: 'wrap',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 8,
        [theme.breakpoints.down('xs')]: {
            padding: '0 8px',
        },
    },
    rangeText: {
        color: 'var(--dm-muted, #647184)',
        fontSize: '0.8125rem',
        fontWeight: 600,
        marginRight: theme.spacing(1),
    },
    pageSizeSelect: {
        minWidth: 120,
        marginLeft: 15,
        [theme.breakpoints.down('xs')]: {
            marginLeft: 0,
        },
    },
    loadError: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        width: '100%',
        marginTop: theme.spacing(1),
        color: '#b42318',
        fontSize: '0.8125rem',
    },
    goToPageInput: {
        minWidth: 110,
        width: 110,
        marginLeft: 15,
        borderRadius: 8,
        padding: 8,
        [theme.breakpoints.down('xs')]: {
            marginLeft: 0,
            minWidth: 90,
            width: 90,
        },
    },
}));


const DetailsAnalyticsTab = ({
    title,
    filters,
    allFilterState,
    handleChange,
    type,
    pageName,
    columns,
    loading,
    handleItemClick,
    list,
    label,
    handlePageChange,
    handleSubmitFilter,
    handleClearFilters,
    handleGoToPage,
    handleDownload,
    downloadReportLoading,
    handlePrint,
    printReportLoading,
    appliedPeriodText = '',
    filterError = '',
    showNotStartedCount = true,
    pagination,
    pendingPage = null,
    handlePageSizeChange,
    handleRetry,
}) => {

    const [open, setOpen] = useState(false);
    const [selectedValue, setSelectedValue] = useState([]);
    const [goToPage, setGoToPage] = useState('');
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

    useEffect(() => {
        setGoToPage('');
    }, [list]);

    const handleClose = value => {
        setOpen(false);
        setSelectedValue(value);
    };

    const componentRef = useRef();
    const rows = list || [];
    const pageState = pagination || resolvePagination(
        { filter: filters },
        { pageno: Math.max(0, (parseInt(allFilterState?.pageno, 10) || 1) - 1), perpage: DEFAULT_PAGE_SIZE }
    );
    const totalPages = pageState.total_pages || 0;
    // MUI Pagination is 1-based; API pageno is 0-based.
    const shownPage = Math.min((pendingPage ? pendingPage.pageno : pageState.pageno) + 1, Math.max(totalPages, 1));
    const shownPerpage = pendingPage ? pendingPage.perpage : pageState.perpage;
    const isPrevItem = (itemType) => itemType === 'previous' || itemType === 'first';
    const isNextItem = (itemType) => itemType === 'next' || itemType === 'last';

    const submitGoToPage = () => {
        if (goToPage && !loading) {
            handleGoToPage(goToPage, type);
        }
    };

    const notStartedCount = filters?.not_started_count;
    const showNotStarted = showNotStartedCount
        && notStartedCount !== undefined
        && notStartedCount !== null
        && notStartedCount !== '';

    const filterHeaderActions = (
        <>
            <IconButton className={classes.actionIconBtn} onClick={handlePrint} aria-label="Print">
                {printReportLoading[type] ? <CircularProgress color="primary" size={16} /> :
                    <Print color="primary" fontSize="small" />}
            </IconButton>
            <IconButton className={classes.actionIconBtn} aria-label="ExportCSV" onClick={handleDownload}>
                {downloadReportLoading[type] ? <CircularProgress color="primary" size={16} /> :
                    <GetAppIcon color="primary" fontSize="small" />}
            </IconButton>
        </>
    );

    return (
        <>
            <Grid container ref={componentRef}>
                {(title || showNotStarted) && (
                    <Grid item xs={12}>
                        <div className={classes.titleRow}>
                            {title ? <Typography className={classes.reportTitle}>{title}</Typography> : null}
                            {showNotStarted ? (
                                <Tooltip title={NOT_STARTED_TOOLTIP} placement="top" arrow>
                                    <Typography className={classes.notStartedCount} tabIndex={0}>
                                        {notStartedCount} course assignments with no activity yet
                                    </Typography>
                                </Tooltip>
                            ) : null}
                        </div>
                    </Grid>
                )}
                <CustamizedFilters
                    type={type}
                    filters={filters}
                    pageName={pageName}
                    allFilterState={allFilterState}
                    handleChange={handleChange}
                    handleSubmitFilter={handleSubmitFilter}
                    handleClearFilters={handleClearFilters}
                    headerActions={filterHeaderActions}
                    loading={loading}
                    appliedPeriodText={appliedPeriodText}
                    filterError={handleRetry ? '' : filterError}
                />
                {handleRetry && filterError ? (
                    <div className={classes.loadError} role="alert">
                        <span>{filterError}</span>
                        <Button size="small" color="primary" variant="outlined" onClick={handleRetry} disabled={loading}>
                            Retry
                        </Button>
                    </div>
                ) : null}
                <Grid item xs={12} className={classes.tableWrapper}>
                    <GenericTable
                        columns={withReportChips(columns)}
                        loading={loading}
                        left={true}
                        handleItemClick={handleItemClick}
                        list={rows}
                        applyMinWidth={false}
                        label={label}
                    />
                </Grid>
            </Grid>
            <Grid container className={classes.paginationRow}>
                <div className={classes.paginationWrap}>
                    <Typography className={classes.rangeText} aria-live="polite">
                        {formatShowingRange(pageState, rows.length)}
                    </Typography>
                    {totalPages > 0 && <Pagination
                        size={isMobile ? 'small' : 'medium'}
                        onChange={handlePageChange}
                        showFirstButton
                        showLastButton
                        siblingCount={isMobile ? 0 : 1}
                        boundaryCount={1}
                        count={totalPages}
                        disabled={loading}
                        variant="outlined"
                        page={shownPage}
                        shape="rounded"
                        renderItem={(item) => (
                            <PaginationItem
                                {...item}
                                disabled={item.disabled
                                    || (isPrevItem(item.type) && !pageState.has_previous)
                                    || (isNextItem(item.type) && !pageState.has_next)}
                            />
                        )}
                    />}
                    {totalPages > 1 && <Input
                        label={"Go to Page"}
                        disableUnderline
                        className={classes.goToPageInput}
                        placeholder='Page'
                        disabled={loading}
                        inputProps={{ min: 1, max: totalPages, 'aria-label': 'Go to page' }}
                        onKeyDown={evt => {
                            if (evt.key === 'Enter') {
                                submitGoToPage();
                            }
                        }}
                        onChange={evt => {
                            const value = parseInt(evt.target.value, 10);
                            setGoToPage(value >= 1 && value <= totalPages ? value : '');
                        }}
                        type={'number'}
                        value={goToPage}
                    />}
                    {handlePageSizeChange && <TextField
                        select
                        size="small"
                        variant="outlined"
                        label="Rows per page"
                        className={classes.pageSizeSelect}
                        value={shownPerpage}
                        disabled={loading}
                        SelectProps={{ native: true }}
                        inputProps={{ 'aria-label': 'Rows per page' }}
                        onChange={evt => handlePageSizeChange(parseInt(evt.target.value, 10), type)}
                    >
                        {PAGE_SIZE_OPTIONS.map((size) => (
                            <option key={size} value={size}>{size}</option>
                        ))}
                    </TextField>}
                </div>
            </Grid>
            <FilterModal
                selectedValue={selectedValue}
                open={open}
                onClose={handleClose}
                allColumns={columns}
            />
        </>
    );
}

export default DetailsAnalyticsTab;
